import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useNotifications } from '../../hooks/useNotifications'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'

const NotificationBell = ({ tone = 'light' }) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)
  const { notifications, unreadCount, markAsRead, markAllAsRead, isConnected, reconnect } = useNotifications()
  const navigate = useNavigate()

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const getNotificationIcon = (tipo) => {
    const icons = {
      success: (
        <div className="w-9 h-9 rounded-xl bg-success-soft text-success-dark flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      ),
      warning: (
        <div className="w-9 h-9 rounded-xl bg-warning-soft text-warning-dark flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
      ),
      error: (
        <div className="w-9 h-9 rounded-xl bg-danger-soft text-danger-dark flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      ),
      info: (
        <div className="w-9 h-9 rounded-xl bg-info-soft text-info-dark flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      )
    }
    return icons[tipo] || icons.info
  }

  // Color del ícono según el fondo donde se monta la campana:
  // 'light' = sobre fondo de color (header móvil), 'dark' = sobre fondo blanco (topbar desktop).
  const bellBtnClass =
    tone === 'dark'
      ? 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
      : 'text-white/90 hover:text-white hover:bg-white/10'

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notificaciones"
        className={`relative p-2 rounded-lg transition-colors ${bellBtnClass}`}
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>

        {/* Unread count badge */}
        <span className="sr-only" aria-live="polite">
          {unreadCount > 0 ? `${unreadCount} notificaciones sin leer` : 'Sin notificaciones nuevas'}
        </span>
        {unreadCount > 0 && (
          <span aria-hidden="true" className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 bg-danger text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-white/20">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}

        {/* Connection status indicator */}
        <span className={`absolute bottom-1 right-1 w-2 h-2 rounded-full ${isConnected ? 'bg-emerald-400' : 'bg-slate-400'}`} title={isConnected ? 'Conectado' : 'Desconectado'} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-[22rem] sm:w-96 max-w-[calc(100vw-1.5rem)] bg-surface rounded-2xl shadow-card-hover border border-hairline z-50 overflow-hidden animate-slide-up">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-hairline">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-ink">Notificaciones</h3>
              {unreadCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-social-100 text-social-700 text-[11px] font-bold">
                  {unreadCount}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs font-semibold text-social-700 hover:text-social-800 hover:underline"
              >
                Marcar todas leídas
              </button>
            )}
          </div>

          {/* Notifications list */}
          <div className="max-h-[26rem] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-slate-50 border border-hairline flex items-center justify-center mb-3">
                  <svg className="w-7 h-7 text-ink-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <p className="text-sm font-semibold text-ink">Estás al día</p>
                <p className="text-xs text-ink-muted mt-0.5">No hay notificaciones nuevas</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <button
                  key={notification.id}
                  type="button"
                  onClick={() => {
                    markAsRead(notification.id)
                    setIsOpen(false)

                    let personaId = null
                    try {
                      const dataObj = typeof notification.datos === 'string'
                        ? JSON.parse(notification.datos)
                        : notification.datos
                      personaId = dataObj?.personaId || dataObj?.persona_id
                    } catch (e) {
                      console.warn('Error parsing notification datos:', e)
                    }

                    if (personaId) {
                      navigate(`/persona/${personaId}`)
                    } else {
                      navigate('/beneficiarios')
                    }
                  }}
                  className={`w-full flex items-start gap-3 px-4 py-3 hover:bg-canvas text-left transition-colors border-b border-hairline/60 last:border-0 ${!notification.leida ? 'bg-social-50/40' : ''
                    }`}
                >
                  {getNotificationIcon(notification.tipo)}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${!notification.leida ? 'font-bold text-ink' : 'font-medium text-ink-soft'}`}>
                      {notification.titulo}
                    </p>
                    <p className="text-sm text-ink-soft truncate">{notification.mensaje}</p>
                    <p className="text-xs text-ink-muted mt-1">
                      {formatDistanceToNow(new Date(notification.created_at), {
                        addSuffix: true,
                        locale: es
                      })}
                    </p>
                  </div>
                  {!notification.leida && (
                    <span className="w-2 h-2 bg-social-600 rounded-full mt-1.5 flex-shrink-0" aria-hidden="true" />
                  )}
                </button>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2.5 border-t border-hairline bg-slate-50/60">
            <p className="text-xs text-ink-muted text-center flex items-center justify-center gap-1.5">
              <span className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-emerald-500' : 'bg-slate-400'}`} aria-hidden="true" />
              {isConnected ? 'Conectado en tiempo real' : 'Reconectando…'}
              {!isConnected && (
                <button type="button" onClick={reconnect} className="ml-1 font-semibold text-social-700 hover:text-social-800 hover:underline">
                  Reintentar
                </button>
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default NotificationBell
